import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { apiClient } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { isIOSApp } from "@/utils/platform";
import { Loader2 } from "lucide-react";

/**
 * « Continuer avec Apple » — affiché UNIQUEMENT dans l'app iOS native
 * (App Store Guideline 4.8 : obligatoire dès qu'un login social tiers existe).
 * Flux natif : plugin AuthenticationServices → identityToken →
 * POST /auth/apple/session (vérification JWKS Apple côté backend).
 */
export default function AppleLoginButton({ label }) {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [loading, setLoading] = useState(false);
  if (!isIOSApp) return null;

  const signIn = async () => {
    setLoading(true);
    try {
      const { SignInWithApple } = await import("@capacitor-community/apple-sign-in");
      const { data: n } = await apiClient.get("/auth/apple/nonce");
      const result = await SignInWithApple.authorize({
        clientId: "gn.neldaari.app",
        redirectURI: "",
        scopes: "email name",
        state: Math.random().toString(36).slice(2),
        nonce: n.nonce_hash,
      });
      const r = result?.response;
      if (!r?.identityToken) throw new Error("no identityToken");
      const { data: user } = await apiClient.post("/auth/apple/session", {
        identity_token: r.identityToken,
        nonce_hash: n.nonce_hash,
        given_name: r.givenName || null,
        family_name: r.familyName || null,
      });
      setUser(user);
      navigate("/", { replace: true });
    } catch (e) {
      // Annulation par l'utilisateur (code 1001) : retour silencieux à l'écran de connexion
      const msg = String(e?.message || e?.code || e);
      if (!/cancel|annul|1001/i.test(msg)) toast.error(label?.includes("Apple") ? "Connexion Apple impossible. Réessayez." : "Apple sign-in failed. Please retry.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={signIn}
      disabled={loading}
      data-testid="login-apple-btn"
      className="w-full mb-3 rounded-full bg-black text-white py-3 font-medium inline-flex items-center justify-center gap-2 text-sm disabled:opacity-60"
    >
      {loading ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : (
        <svg width="16" height="18" viewBox="0 0 814 1000" fill="currentColor" aria-hidden="true">
          <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76.5 0-103.7 40.8-165.9 40.8s-105.6-57-155.5-127C46.7 790.7 0 663 0 541.8c0-194.4 126.4-297.5 250.8-297.5 66.1 0 121.2 43.4 162.7 43.4 39.5 0 101.1-46 176.3-46 28.5 0 130.9 2.6 198.3 99.2zm-234-181.5c31.1-36.9 53.1-88.1 53.1-139.3 0-7.1-.6-14.3-1.9-20.1-50.6 1.9-110.8 33.7-147.1 75.8-28.5 32.4-55.1 83.6-55.1 135.5 0 7.8 1.3 15.6 1.9 18.1 3.2.6 8.4 1.3 13.6 1.3 45.4 0 102.5-30.4 135.5-71.3z" />
        </svg>
      )}
      {label}
    </button>
  );
}
